import { QueryHookOptions, gql } from '@apollo/client'
import { usePersistedCacheQuery } from 'app/hooks'
import { GroupEvent, GroupEventRsvps } from 'app/types'
import { GroupVisibility } from 'app/types/GroupVisibility'

const query = gql`
  query groupEventById($id: String!) {
    groupEventById(id: $id) {
      groupPost {
        id
        creator {
          id
          fullName
          avatar {
            id
            sizes {
              small {
                url
              }
              medium {
                url
              }
            }
          }
          university {
            id
            name
          }
        }
      }
      event {
        id
        name
        description
        startDate
        endDate
        createdAt
        location
        image {
          id
          url
          width
          height
        }
        group {
          id
          name
          totalMembers
          visibility
          profileImage {
            id
            sizes {
              small {
                url
              }
              medium {
                url
              }
            }
          }
          university {
            slug
          }
          universityCourse {
            slug
          }
          allowedUserTypes
          isOwner
        }
      }
      rsvpCount {
        going
        maybe
      }
      rsvps(limit: 3, page: 1, status: [going, maybe]) {
        user {
          id
          fullName
          avatar {
            id
            sizes {
              small {
                url
              }
              medium {
                url
              }
            }
          }
          university {
            id
            name
          }
        }
      }
    }
  }
`

export interface GroupEventQueryInput {
  id: string
}

export interface GroupEventQueryOutput {
  groupEventById: {
    groupPost: {
      id: string
      creator: {
        id: string
        fullName: string
        avatar?: {
          id: string
          sizes: {
            small: {
              url: string
            }
            medium: {
              url: string
            }
          }
        }
        university?: {
          id: string
          name: string
        }
      }
    }
    event: {
      id: string
      name: string
      description: string
      location?: string
      startDate: string
      endDate?: string
      createdAt: string
      image?: {
        id: string
        url: string
        width: number
        height: number
      }
      group: {
        id: string
        name: string
        visibility: GroupVisibility
        totalMembers: number
        profileImage: {
          sizes: {
            small: {
              url: string
            }
            medium: {
              url: string
            }
          }
        }
        university?: {
          slug: string
        }
        universityCourse?: {
          slug: string
        }
        allowedUserTypes: string[]
        isOwner: boolean
      }
    }
    rsvpCount: {
      going: number
      maybe: number
    }
    rsvps: {
      user: {
        id: string
        fullName: string
        avatar?: {
          id: string
          sizes: {
            small: {
              url: string
            }
            medium: {
              url: string
            }
          }
        }
        university?: {
          id: string
          name: string
        }
      }
    }[]
  }
}

type FormatFnOutput = {
  event: GroupEvent
  rsvps: GroupEventRsvps
}

const formatFn = (data?: GroupEventQueryOutput): FormatFnOutput | undefined => {
  if (!data) {
    return undefined
  }

  const { event, rsvps, rsvpCount, groupPost } = data.groupEventById
  return {
    event: {
      type: 'groupEvent',
      href: `/groups/events/${event.id}`,
      id: groupPost.id,
      eventId: event.id,
      name: event.name,
      description: event.description,
      location: event.location,
      startDate: event.startDate,
      endDate: event.endDate,
      createdAt: event.createdAt,
      creator: {
        id: groupPost.creator.id,
        fullName: groupPost.creator.fullName,
        ...(groupPost.creator.avatar && {
          avatar: {
            medium: groupPost.creator.avatar?.sizes?.medium.url,
            small: groupPost.creator.avatar?.sizes?.small.url,
          },
        }),
      },
      group: {
        id: event.group.id,
        name: event.group.name,
        href: `/groups/${event.group.id}`,
        profileImage: {
          small: event.group.profileImage?.sizes.small.url || '',
        },
        university: event.group.university,
        universityCourse: event.group.universityCourse,
        allowedUserTypes: event.group.allowedUserTypes || [],
        isOwner: event.group.isOwner,
      },
      ...(event.image && {
        image: {
          url: event.image.url,
          width: event.image.width,
          height: event.image.height,
        },
      }),
    },
    rsvps: {
      going: rsvpCount.going || 0,
      maybe: rsvpCount.maybe || 0,
      lastUsers:
        rsvps?.map(({ user }) => ({
          id: user.id,
          fullName: user.fullName,
          university: user.university,
          ...(user.avatar && {
            avatar: {
              small: user.avatar.sizes?.small?.url,
              medium: user.avatar.sizes?.medium?.url,
            },
          }),
        })) || [],
    },
  }
}

export const useGroupEventQuery = (
  options: QueryHookOptions<GroupEventQueryOutput, GroupEventQueryInput>
) => usePersistedCacheQuery(query, options)

export const groupEventByIdQuery = {
  query,
  formatFn,
}
